import type { LibraryRef } from '@/types/shared'

export const LIBRARY_VIDEO_DRAG_MIME = 'application/x-martini-library-video'

export type LibraryVideoDragItem = {
  assetId: string
  videoUrl: string
  displayName?: string
  thumbnailUrl?: string
  width?: number
  height?: number
  libraryRef?: LibraryRef
}

type UnknownRecord = Record<string, unknown>

function asOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

function asOptionalPositiveNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined
}

function parseLibraryRef(value: unknown): LibraryRef | undefined {
  if (!value || typeof value !== 'object') return undefined
  const raw = value as UnknownRecord
  const collectionId = asOptionalString(raw.collectionId)
  if (!collectionId) return undefined
  const assetId = asOptionalString(raw.assetId)
  const versionId = asOptionalString(raw.versionId)
  return {
    collectionId,
    ...(assetId ? { assetId } : {}),
    ...(versionId ? { versionId } : {}),
  }
}

function parseItem(value: unknown): LibraryVideoDragItem | null {
  if (!value || typeof value !== 'object') return null
  const raw = value as UnknownRecord
  const assetId = asOptionalString(raw.assetId)
  const videoUrl = asOptionalString(raw.url)
  if (!assetId || !videoUrl) return null
  const displayName = asOptionalString(raw.name)
  const thumbnailUrl = asOptionalString(raw.thumbnailUrl)
  const width = asOptionalPositiveNumber(raw.width)
  const height = asOptionalPositiveNumber(raw.height)
  const libraryRef = parseLibraryRef(raw.libraryRef)
  return {
    assetId,
    videoUrl,
    ...(displayName ? { displayName } : {}),
    ...(thumbnailUrl ? { thumbnailUrl } : {}),
    ...(width ? { width } : {}),
    ...(height ? { height } : {}),
    ...(libraryRef ? { libraryRef } : {}),
  }
}

/**
 * Decode the Collections shelf's raw-video drag contract. Treat the payload as
 * untrusted browser data: malformed entries are ignored, while a valid subset
 * of a multi-selection remains usable.
 */
export function parseLibraryVideoDragPayload(encoded: string): LibraryVideoDragItem[] {
  try {
    const payload = JSON.parse(encoded) as UnknownRecord
    if (payload.type === 'library-video') {
      const item = parseItem(payload)
      return item ? [item] : []
    }
    if (payload.type === 'library-videos' && Array.isArray(payload.items)) {
      return payload.items.map(parseItem).filter((item): item is LibraryVideoDragItem => item !== null)
    }
  } catch {
    // Native drops are user-controlled data; invalid JSON is simply not ours.
  }
  return []
}
